import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { z } from 'zod';

import type { EnhancedToolResult } from '../../types';
import { Tool } from '../Tool';

import type { BrandKit, BrandKitColor } from 'js/src/client';
import { getAllBrandKits } from 'js/src/data/brand-kits/brand-kits-selectors';

export const listBrandKitsSchema = z.object({});
export type ListBrandKitsArgs = z.infer<typeof listBrandKitsSchema>;

function colorHex({ r, g, b }: BrandKitColor): string {
    return `#${[r, g, b].map((channel) => channel.toString(16).padStart(2, '0')).join('')}`;
}

// A workspace can hold dozens of kits, and the full records carry watermark media, licence and
// credit fields; listed whole they filled the result to its cap and rode along in every later turn.
// Applying a kit takes only its id, so the listing carries what choosing one needs.
function summarizeBrandKit(kit: BrandKit) {
    return {
        id: kit.id,
        name: kit.name,
        colors: kit.colors.map((color) => ({ hex: colorHex(color), category: color.category })),
        hasWatermark: Boolean(kit.watermark),
    };
}

export class ListBrandKitsTool extends Tool<typeof listBrandKitsSchema> {
    protected override readonly schema = listBrandKitsSchema;
    protected override readonly toolName = TOOL_NAMES.listBrandKits;
    protected override readonly toolDescription =
        "List all of the current team's saved brand kits.";

    public override getToolDisplayName(): string {
        return 'Listing brand kits';
    }

    public get statusIcon(): string {
        return 'palette';
    }

    public override async execute(): Promise<EnhancedToolResult> {
        const brandKits = getAllBrandKits(this.getState())
            .map((kit) => summarizeBrandKit(kit.toJS()))
            .toArray();
        return {
            result: JSON.stringify(brandKits),
            isSuccess: true,
        };
    }
}
